import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { getPostBySlug } from './blogPosts';
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, CheckCircle2, BookOpen } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | INeedASocialWorker.com`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = post.excerpt;

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://ineedasocialworker.com/blog/${post.slug}`;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      author: { '@type': 'Organization', name: 'INeedASocialWorker.com' },
      publisher: {
        '@type': 'Organization',
        name: 'INeedASocialWorker.com',
        logo: { '@type': 'ImageObject', url: 'https://ineedasocialworker.com/inasw-logo-crisp.png' },
      },
      mainEntityOfPage: `https://ineedasocialworker.com/blog/${post.slug}`,
    };
    let schemaScript = document.getElementById('article-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'article-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schema);

    return () => {
      const existing = document.getElementById('article-schema');
      if (existing) existing.remove();
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid={`blog-post-${post.slug}`}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary to-[#0F2E24] py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300 mb-4">
            <span className="inline-flex items-center gap-1"><Tag className="w-4 h-4" />{post.category}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{post.date}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{post.readTime}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white leading-tight">{post.title}</h1>
        </div>
      </section>

      {/* Body */}
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">{post.excerpt}</p>

          {post.sections.map((section, i) => (
            <section key={i} className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-3">{section.heading}</h2>
              {section.paragraphs?.map((p, j) => (
                <p key={j} className="text-gray-700 leading-relaxed mb-4">{p}</p>
              ))}
              {section.list && (
                <ul className="space-y-2 mb-2">
                  {section.list.map((item, k) => (
                    <li key={k} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* CTA */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mt-10">
            <h3 className="font-semibold text-gray-900 mb-2">Ready to find your next role?</h3>
            <p className="text-gray-600 mb-4">Create a free candidate profile and apply in seconds with SW Express Apply™.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              {post.relatedLink && (
                <Link to={post.relatedLink.href}>
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="blog-related-cta">
                    {post.relatedLink.label}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              <Link to="/register">
                <Button className="bg-secondary hover:bg-secondary-hover text-white">
                  Create Free Account
                </Button>
              </Link>
            </div>
          </div>

          {/* Back to blog */}
          <div className="text-center mt-12">
            <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:underline">
              <BookOpen className="w-4 h-4" />
              Read more career articles
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
